import { useCallback, useEffect } from 'react'

import useQuery from 'common/use-query-params'
import { HistoryLocation, useHistory } from 'components/ReactRouterProvider/context'

import { SortDirection } from '.'

interface URLTableState {
  page: number
  pageSize: number
  sort: string
  sortDir: SortDirection
}
type URLTableStateUpdate = Partial<URLTableState>

const updateTableState = (
  id: string,
  newState: URLTableStateUpdate | undefined,
  query: URLSearchParams,
  history: HistoryLocation,
) => {
  const currentQuery = query.get(id)
  let oldState: URLTableStateUpdate = {}
  if (currentQuery) {
    oldState = JSON.parse(currentQuery)
  }
  query.delete(id)
  if (newState) {
    const stringState = JSON.stringify({
      ...oldState,
      ...newState,
    })
    query.append(id, stringState)
  }
  history.replace(`${history.location.pathname}?${query.toString()}`)
}

const useTableContext = ({
  id = 't',
  defaultSortDir = 'asc',
  defaultSortKey,
  page = 0,
  pageSize = 10,
}: {
  id?: string
  defaultSortDir?: SortDirection
  defaultSortKey: string
  page?: number
  pageSize?: number
}) => {
  const query = useQuery()
  const history = useHistory()
  const tableQueryString = query.get(id)
  let newState: URLTableState = {
    page,
    pageSize,
    sort: defaultSortKey,
    sortDir: defaultSortDir,
  }
  const updateContext = useCallback(
    (ctx?: URLTableStateUpdate) => updateTableState(id, ctx, query, history),
    [id, query, history],
  )
  if (tableQueryString) {
    newState = {
      ...newState,
      ...JSON.parse(tableQueryString),
    }
  }
  useEffect(() => {
    return () => {
      const params = new URLSearchParams(history.location.search)
      params.delete(id)
      history.replace(`${history.location.pathname}?${params.toString()}`)
    }
  }, [history, id])
  return {
    ...newState,
    updateContext,
  }
}
export default useTableContext
